// src/components/FeedVideo.js
//
// IG otomatiği — feed videosu: görünür olunca SESSİZ otomatik oynar,
// görünümden çıkınca durur (IntersectionObserver). Kontrol çubuğu yok
// (IG paritesi); sağ alttaki hoparlör butonu sesi açar/kapar ve tercih
// kalıcıdır (localStorage — clips'teki desenle aynı).
//
// FLICKER FIX (2026-06-30) — eskiden TEK eşikte (%50) ham IO sonucu direkt
// play()/pause()'a bağlıydı: scroll sırasında kart %50 sınırı civarında
// salınınca (momentum/layout-shift) inView art arda flip oluyor, her flip
// play()/pause() tetikliyordu → play() Promise'i henüz settle olmadan
// pause() çağrılıp AbortError zinciri + siyah-kare flaşı ("floresan lamba"
// etkisi) oluşuyordu. 10-ajanlı araştırma (kod-teşhis + Instagram/web
// mühendisliği referansı) aynı çözümde birleşti: ASİMETRİK ÇİFT-EŞİK
// (histerezis) + KISA STATE-DEBOUNCE + play() Promise'ini bekleyip ONDAN
// SONRA pause() çağırma disiplini. Bkz. ENTER/EXIT_THRESHOLD, DEBOUNCE_MS.

import React, { useEffect, useRef, useState, useCallback } from "react";
import { clampRatio } from "./PostImage";
// PR-33 — feed video preload'u costMode'a bağlanır (limited/emergency → none).
import useCostMode, { getCostModeConfig } from "../hooks/useCostMode";
import { hataAlani } from "../utils/hataAlani";
import "./FeedVideo.css";
import { IkonKoprusu } from "../icon-system/runtime/IkonKoprusu";
import { boyutCoz } from "../icon-system/adapters/boyut";

const MUTE_KEY = "mylasa.feed.mute.preferred";
// Histerezis: girişte yüksek bar, çıkışta düşük bar — aradaki "ölü bölge"
// (0.3–0.6) scroll salınımında karar DEĞİŞTİRMEZ, mevcut durum korunur.
const ENTER_THRESHOLD = 0.6;
const EXIT_THRESHOLD = 0.3;
// Niyet değiştiğinde hemen uygulanmaz; bu süre kesintisiz sabit kalırsa
// gerçek play()/pause() tetiklenir (kısa salınımları tamamen eler).
// Asimetrik: çıkış girişten daha hızlı (gereksiz ses/CPU'yu erken kes).
const ENTER_DEBOUNCE_MS = 200;
const EXIT_DEBOUNCE_MS = 100;

function readMutePref() {
  try { return localStorage.getItem(MUTE_KEY) !== "false"; } catch { return true; }
}

export type FeedVideoProps = {
  src?: string;
  className?: string;
  onCanPlay?: React.ReactEventHandler<HTMLVideoElement>;
  onClick?: React.MouseEventHandler<HTMLVideoElement>;
  poster?: string;
  /** Post dökümanındaki ölçüler — `clampRatio` ile aynı sözleşme. */
  w?: number | null;
  h?: number | null;
};

/** Sesi aç/kapat rozeti 14 px: token dışı (16/20/24/32) → px korunur + `data-boyut-px="14"` (modül-seviye; render başına nesne yok). */
const _b14 = boyutCoz(14);
const MOTOR_BOYUT_14 = { boyut: _b14.boyut, kutuPx: _b14.kutuPx, "data-boyut-px": _b14.borc };

export default function FeedVideo({ src, className = "", onCanPlay, onClick, poster, w, h }: FeedVideoProps) {
  // PR-33 — preload costMode'a bağlı (singleton snapshot; ek maliyet yok).
  const costMode = useCostMode();
  const { videoPreload } = getCostModeConfig(costMode);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(readMutePref);
  const [inView, setInView] = useState(false);
  // IG oran kuralı: kapsayıcı videonun clamp'lenmiş kendi oranında —
  // yan bant oluşmaz (bkz. PostImage). w/h verilmişse CLS'siz başlar.
  const declared = clampRatio(w, h);
  const [measured, setMeasured] = useState<number | null>(null);
  const ratio = declared || measured || 1;

  const handleMeta = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    // React `SyntheticEvent.target`i `EventTarget`; ölçüm alanları burada
    // daraltılıyor. `currentTarget`e GEÇMEK davranış değişikliği olurdu.
    const t = e?.target as HTMLVideoElement | undefined;
    const r = clampRatio(t?.videoWidth, t?.videoHeight);
    if (r && !declared) setMeasured(r);
  }, [declared]);

  // Histerezis state-machine: ham intersectionRatio'yu okuyup "ölü bölge"de
  // (EXIT_THRESHOLD..ENTER_THRESHOLD arası) mevcut hedefi KORUR; hedef
  // değiştiğinde de asimetrik debounce ile kısa-süreli salınımları eler.
  // targetIntentRef: en son KARARLAŞTIRILAN hedef niyet — commit edilmiş
  // (timer henüz kurulmadıysa) VEYA şu an bir debounce timer'ının hedeflediği
  // değer (timer bekliyorsa). KRİTİK: yeni entry geldiğinde bunun KARŞISINDA
  // karşılaştırma yapılır (commit edilmiş değere değil) — aksi halde aynı
  // yönde art arda gelen entry'ler her seferinde timer'ı sıfırlayıp niyet
  // ASLA commit olmaz (kendi-içinde sonsuz erteleme bug'ı).
  const targetIntentRef = useRef(false);
  useEffect(() => {
    const el = videoRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
        let nextIntent = targetIntentRef.current;
        if (ratio >= ENTER_THRESHOLD) nextIntent = true;
        else if (ratio < EXIT_THRESHOLD) nextIntent = false;
        // EXIT..ENTER arası (ölü bölge): nextIntent değişmez, mevcut hedef korunur.

        if (nextIntent === targetIntentRef.current) return; // zaten bu yönde hedeflenmiş — no-op

        targetIntentRef.current = nextIntent;
        if (timer) clearTimeout(timer);
        const delay = nextIntent ? ENTER_DEBOUNCE_MS : EXIT_DEBOUNCE_MS;
        timer = setTimeout(() => {
          timer = null;
          setInView(nextIntent);
        }, delay);
      },
      { threshold: [0, EXIT_THRESHOLD, ENTER_THRESHOLD, 1] }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  // play()/pause() Promise-güvenli sıralama: pause() çağrısı, önceki play()
  // isteği SETTLE olana kadar ertelenir (Chrome'un resmi önerisi — aksi halde
  // "play() request was interrupted by pause()" AbortError zinciri + görsel
  // stutter/siyah-kare oluşur). AbortError sessizce yutulur (beklenen/zararsız);
  // yalnız gerçek autoplay-block (NotAllowedError) sessize-alıp yeniden dener.
  const playPromiseRef = useRef<Promise<void> | null>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    let cancelled = false;

    if (inView) {
      el.muted = muted;
      const p = el.play?.();
      playPromiseRef.current = p || null;
      if (p && typeof p.catch === "function") {
        p.catch((err: unknown) => {
          if (cancelled) return;
          // `hataAlani(err,"name")` = `err?.name` (dize karşılaştırması aynı).
          if (hataAlani(err, "name") === "AbortError") return; // pause() ile kesildi — zararsız
          // Gerçek autoplay-block (NotAllowedError vb.): sessize al, tekrar dene.
          el.muted = true;
          setMuted(true);
          el.play?.().catch(() => {});
        });
      }
    } else {
      const prevPlay = playPromiseRef.current;
      if (prevPlay && typeof prevPlay.then === "function") {
        prevPlay.finally(() => {
          if (!cancelled) el.pause?.();
        }).catch(() => {});
      } else {
        el.pause?.();
      }
    }

    return () => { cancelled = true; };
  }, [inView, muted]);

  const toggleMute = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem(MUTE_KEY, String(next)); } catch { /* no-op */ }
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  }, []);

  return (
    <div className="feed-video-wrap" style={{ aspectRatio: `1 / ${ratio}` }}>
      <video
        ref={videoRef}
        src={src}
        className={className}
        poster={poster || undefined}
        muted={muted}
        loop
        playsInline
        preload={videoPreload}
        onLoadedMetadata={handleMeta}
        onCanPlay={onCanPlay}
        onClick={onClick}
      />
      <button
        type="button"
        className="feed-video-mute pressable"
        onClick={toggleMute}
        aria-label={muted ? "Sesi aç" : "Sesi kapat"}
      >
        {/* FAZ C / DALGA 2 — "elle 1" (GECIS_PLANI: width/height=14 token dışı). Dalga-1
            BottomNav `size=28` deseni: motor kolunda px KORUNUR + `data-boyut-px="14"`
            (borç DOM'da görünür; token'a çekmek owner önizlemesi ister). Bayrak kapalı →
            `eski` (inline svg, dokunulmadan) → piksel-birebir. */}
        {muted ? (
          <IkonKoprusu aile="media.mute" {...MOTOR_BOYUT_14} eski={<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.59 3 2.7 2.7-1.41 1.42-2.7-2.71-2.71 2.71-1.41-1.42 2.7-2.7-2.7-2.7 1.41-1.42 2.71 2.71 2.7-2.71 1.41 1.42-2.7 2.7z"/></svg>} />
        ) : (
          <IkonKoprusu aile="media.volume" {...MOTOR_BOYUT_14} eski={<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>} />
        )}
      </button>
    </div>
  );
}
